import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react'

import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import HStack from '@jimmyboys/liita-ui/dist/components/HStack'

import HFQuantitiesGroup from 'components/HFInputs/HFQuantitiesGroup'
import HFTextInput from 'components/HFInputs/HFTextInput'
import styles from '../Dialog.module.sass'
import ExpandableWidget from 'components/ExpandableWidget/ExpandableWidget'
import ModalOptions from '@jimmyboys/liita-ui/dist/components/ModalOptions'
import TaskPaymentsController from 'controllers/TaskManager/TaskSubControllers/TaskPaymentsController/TaskPaymentsController'
import { TaskForwardingController } from 'controllers/TaskManager/TaskSubControllers/TaskForwardingController/TaskForwardingController'
import VStack from '@jimmyboys/liita-ui/dist/components/VStack'
import { TaskNeedsController } from 'controllers/TaskManager/TaskSubControllers/TaskNeedsController/TaskNeedsController'
import { useFormContext, useWatch } from 'react-hook-form'
import { API_URL } from 'store/fetchUtils'
import Icon from '@jimmyboys/liita-ui/dist/components/Icon'
import JustificationField from 'components/JustificationField/JustificationField'
import { useJustifications } from 'hooks/useJustifications'
import AlertBox from 'components/AlertBox/AlertBox'

export const MedicalActsClinicalApprovalForm = (props) => {
	const { i18n } = useGlobal()
	const { data } = props
	const {
		requisition,
		isAdministrative,
		outsideNetwork,
		districts,
		serviceProviders,
		isFromIsland,
		requiresChargeAmount,
		establishments,
		accidentType,
		postalCode,
		medicalActs,
		onUpdateFormData,
		onResolverDisabled = () => {},
		appointmentContent,
		processContent,
		validateRef,
	} = data

	const jsonResponseRef = useRef(null)

	const injuredBirthday = processContent?.injured?.birthday
	const injuredType = processContent?.info?.injuryType?.id
	const bodyPart = processContent?.info?.bodyPart?.id

	const getSymbol = (value, className) => {
		switch (value) {
			case 0:
				return (
					<>
						<Icon
							name='fast-upward-one'
							style={{
								fill: className,
								marginRight: '5px',
							}}
							width='14px'
							height='14px'
						/>
					</>
				)
			case 1:
				return (
					<>
						<Icon
							name='fast-upward-two'
							style={{
								fill: className,
								marginRight: '5px',
							}}
							width='14px'
							height='14px'
						/>
					</>
				)

			case 2:
				return (
					<>
						<Icon
							name='fast-downward-one'
							style={{
								fill: className,
								marginRight: '5px',
							}}
							width='14px'
							height='14px'
						/>
					</>
				)
			case 3:
				return (
					<>
						<Icon
							name='fast-downward-two'
							style={{
								fill: className,
								marginRight: '5px',
							}}
							width='14px'
							height='14px'
						/>
					</>
				)
			case 4:
				return (
					<>
						<Icon
							name='equals'
							style={{
								fill: className,
								marginRight: '5px',
							}}
							width='16px'
							height='16px'
						/>
					</>
				)
			default:
				return null
		}
	}

	const { setValue } = useFormContext()
	const medicalActsFormValue = useWatch({ name: 'medicalActs' })
	const [selectedProvider, setSelectedProvider] = useState(null)

	const selectedMedicalActCodes = useMemo(() => {
		if (!medicalActsFormValue || !medicalActs) return medicalActs ?? []

		return medicalActs.filter((_, index) => {
			return medicalActsFormValue[index]?.checked
		})
	}, [medicalActsFormValue, medicalActs])

	const [prestadorData, setPrestadorData] = useState('')
	const [isLoading, setIsLoading] = useState(false)
	const commaIndex = prestadorData.lastIndexOf(';,')
	const mensagemFinal =
		commaIndex >= 0 ? prestadorData.substring(commaIndex + 2) : ''
	const [requestData, setRequestData] = useState(null)
	const dataUpdateRef = useRef(false)

	const [pendingProvider, setPendingProvider] = useState(null)
	const [selectedUserJustification, setSelectedUserJustification] =
		useState(null)
	const [selectedUserJustificationId, setSelectedUserJustificationId] =
		useState(null)
	const numSeguradoraRef = useRef(null)
	const formattedDateAppointmentRef = useRef(null)
	const formattedDateScheduledRef = useRef(null)
	const usernameRef = useRef(null)
	const onUpdateFormDataRef = useRef(onUpdateFormData)
	onUpdateFormDataRef.current = onUpdateFormData
	const prevDataToSendRef = useRef(null)
	const { justifications } = useJustifications(1)
	const [blockCount, setBlockCount] = useState(0)
	const [showWarning, setShowWarning] = useState(false)

	const handleJustificationChange = (justification) => {
		if (!justification) {
			setSelectedUserJustification(null)
			setSelectedUserJustificationId(null)
			return
		}
		setSelectedUserJustification(justification.justificationName)
		setSelectedUserJustificationId(justification.justificationId)
	}

	const handleJustificationCancel = () => {
		setPendingProvider(null)
		setSelectedProvider(null)
		setSelectedUserJustification(null)
		setSelectedUserJustificationId(null)
		setValue('providerId', null)
	}

	const handleProviderPendingChange = useCallback((provider) => {
		setPendingProvider(provider)
		if (!provider) {
			setSelectedUserJustification(null)
			setSelectedUserJustificationId(null)
		}
	}, [])

	useEffect(() => {
		setShowWarning(false)
	}, [pendingProvider])

	useEffect(() => {
		setShowWarning(false)
	}, [blockCount])

	useEffect(() => {
		if (validateRef) {
			validateRef.current = () => {
				// The form's own JustificationField is only rendered for type 2.
				// For other types (e.g. physiotherapy), justification is handled
				// inside TaskForwardingController and tracked via blockCount.
				const formJustificationBlocked =
					requisition?.type?.id === 2 &&
					pendingProvider !== null &&
					!selectedUserJustification
				if (formJustificationBlocked || blockCount > 0) {
					setShowWarning(true)
					return false
				}
				return true
			}
		}
	}, [
		pendingProvider,
		selectedUserJustification,
		validateRef,
		blockCount,
		requisition,
	])

	useEffect(() => {
		onResolverDisabled(false)
	}, [])

	useEffect(() => {
		if (requisition?.status?.id === 2) {
			return
		}

		if (requisition?.type?.id === 2) {
			formattedDateAppointmentRef.current =
				requisition?.appointment?.date?.UTC?.split('T')[0]

			formattedDateScheduledRef.current =
				requisition?.scheduledDate?.UTC?.split('T')[0] || null

			numSeguradoraRef.current =
				processContent?.insurancePolicy?.insuranceCompany?.id

			const numSeguradora = numSeguradoraRef.current
			const formattedDateAppointment = formattedDateAppointmentRef.current
			const formattedDateScheduled = formattedDateScheduledRef.current

			if (
				!numSeguradora ||
				!selectedMedicalActCodes?.length ||
				!postalCode ||
				!accidentType
			) {
				console.error('Dados insuficientes')
				return
			}

			const fetchPy = async () => {
				try {
					setIsLoading(true)
					usernameRef.current = JSON.parse(
						localStorage.getItem('user')
					).username
					const username = usernameRef.current

					const apiUrl = `${API_URL}/api/suggest/imaging/suggestions`
					const bodyData = {
						branch: accidentType,
						insuranceId: Number(numSeguradora),
						codes: selectedMedicalActCodes,
						postalCode7: postalCode,
						requestingProviderId:
							requisition?.requiringEstablishment?.id ||
							undefined,
						appointmentDate: formattedDateAppointment
							? formattedDateAppointment + 'T00:00:00'
							: undefined,
						scheduleDate: formattedDateScheduled
							? formattedDateScheduled + 'T00:00:00'
							: undefined,
					}
					setRequestData(bodyData)

					const response = await fetch(apiUrl, {
						method: 'POST',
						headers: {
							'Content-Type': 'application/json',
						},
						body: JSON.stringify(bodyData),
					})

					if (!response.ok) {
						throw new Error('Network response was not ok')
					}

					const jsonResponse = await response.json()

					jsonResponseRef.current = jsonResponse

					// Only update once
					if (!dataUpdateRef.current && onUpdateFormData) {
						const dataToSend = {
							ramo: accidentType,
							selected_seguradora: Number(
								numSeguradoraRef.current
							),
							select_acto: selectedMedicalActCodes,
							codpost7: postalCode,
							prestador_requerente:
								requisition?.establishment?.id,
							data_consulta: formattedDateAppointmentRef.current,
							data_marcacao: formattedDateScheduledRef.current,
							processId: Number(
								window.location.pathname.split('/')[2]
							),
							requisitionId: requisition?.id,
							username: usernameRef.current,
							response: jsonResponse,
							userJustification: selectedUserJustification,
							userJustificationId: selectedUserJustificationId,
						}

						onUpdateFormData(dataToSend)
						dataUpdateRef.current = true
					}

					const displayText =
						jsonResponse.providerReferrals
							.map((p) => {
								const distanceKm = (p.distance / 1000).toFixed(
									2
								)
								const daysFormatted = p.averageTime.toFixed(2)
								const priceFormatted = p.price.toFixed(2)
								const percentagem = (
									((p.retailPrice - p.price) / p.price) *
									100
								).toFixed(2)

								return `${p.providerName};${priceFormatted};${distanceKm};${daysFormatted};${percentagem};`
							})
							.join('\\n') +
						',' +
						jsonResponse.description

					setPrestadorData(displayText)
				} catch (error) {
					console.error('Erro capturado:', error)
					setPrestadorData(
						'Erro ao carregar prestadores. Tente novamente.'
					)
				} finally {
					setIsLoading(false)
				}
			}
			fetchPy()
		}
	}, [selectedMedicalActCodes])

	useEffect(() => {
		if (dataUpdateRef.current && onUpdateFormDataRef.current) {
			const dataToSend = {
				ramo: accidentType,
				selected_seguradora: Number(numSeguradoraRef.current),
				select_acto: medicalActs,
				codpost7: postalCode,
				prestador_requerente: requisition?.establishment?.id,
				data_consulta: formattedDateAppointmentRef.current,
				data_marcacao: formattedDateScheduledRef.current,
				processId: Number(window.location.pathname.split('/')[2]),
				requisitionId: requisition?.id,
				username: usernameRef.current,
				response: jsonResponseRef.current,
				userJustification: selectedUserJustification,
				userJustificationId: selectedUserJustificationId,
			}

			const currentSerialized = JSON.stringify(dataToSend)
			const prevSerialized = JSON.stringify(
				prevDataToSendRef.current || {}
			)

			if (currentSerialized !== prevSerialized) {
				prevDataToSendRef.current = dataToSend
				onUpdateFormDataRef.current(dataToSend)
			}
		}
	}, [selectedUserJustification, selectedUserJustificationId])

	return (
		<>
			{requisition?.medicalActs && requisition.medicalActs.length > 0 ? (
				<fieldset>
					<HStack gap='xsmall'>
						<h4>{i18n`Medical Acts`}</h4>
						<h4 className={styles['counter']}>
							{requisition?.medicalActs?.length || 0}
						</h4>
					</HStack>
					<VStack>
						<small className={styles['laterality-label']}>
							{i18n`Laterality | Quantity`}
						</small>
						<HFQuantitiesGroup
							tint='accent'
							name={'medicalActs'}
							data={requisition?.medicalActs || []}
							hasLaterality
						/>
					</VStack>
				</fieldset>
			) : null}

			{isFromIsland && (
				<TaskNeedsController
					isAdministrative={isAdministrative}
					requisition={requisition}
				/>
			)}

			{requisition?.type?.id === 2 && requisition?.status?.id !== 2 && (
				<fieldset>
					<VStack>
						<HStack>
							<svg
								xmlns='http://www.w3.org/2000/svg'
								viewBox='0 0 24 24'
								fill='currentColor'
								width='24'
								height='24'
								style={{ fill: '#587587' }}
							>
								<path d='M12 2a1 1 0 011 1v1h4a2 2 0 012 2v3h1a1 1 0 110 2h-1v7a2 2 0 01-2 2h-2v1a1 1 0 11-2 0v-1H9v1a1 1 0 11-2 0v-1H5a2 2 0 01-2-2v-7H2a1 1 0 110-2h1V6a2 2 0 012-2h4V3a1 1 0 011-1zm-4 7a1 1 0 100 2 1 1 0 000-2zm8 0a1 1 0 100 2 1 1 0 000-2zm-8 4h8v5H8v-5z' />
							</svg>
							<h4 className={styles['noMargin']}>
								{i18n`Trust AI Tittle`}
							</h4>
						</HStack>
						<h4>{i18n`Trust AI Suggestion`}</h4>
					</VStack>
					{isLoading ? (
						<p>Loading...</p>
					) : (
						<>
							<h6 className={styles['noMargin']}>
								{i18n`Trust AI Sections`}
							</h6>
							{prestadorData &&
							prestadorData !==
								'Erro ao carregar prestadores. Tente novamente.' ? (
								prestadorData
									.split('\\n')
									.map((line, lineIndex) => {
										if (line.includes(';')) {
											const rowData = line.split(';')
											const prestador = rowData[0]
											const valorData = rowData[1]
											const distancia = rowData[2]
											const rapidez = rowData[3]
											const percentagem = parseFloat(
												rowData[4]
											)
											let symbol = null
											let className = 'currentColor'
											switch (true) {
												case percentagem > -10 &&
													percentagem <= 10:
													symbol = getSymbol(
														4,
														'#d3b610'
													)
													break
												case percentagem <= -10 &&
													percentagem > -30:
													className = '#8B0000'
													symbol = getSymbol(
														2,
														className
													)
													break
												case percentagem <= -30:
													className = '#8B0000'
													symbol = getSymbol(
														3,
														className
													)
													break
												case percentagem > 10 &&
													percentagem <= 30:
													className = '#23a823'
													symbol = getSymbol(
														0,
														className
													)
													break
												case percentagem > 30:
													className = '#23a823'
													symbol = getSymbol(
														1,
														className
													)
													break
												default:
													break
											}
											const provider =
												jsonResponseRef.current
													?.providerReferrals?.[
													lineIndex
												]
											const firstProvider =
												jsonResponseRef.current
													?.providerReferrals?.[0]
											const isFirstProvider =
												firstProvider &&
												provider &&
												Number(
													firstProvider.providerId
												) ===
													Number(provider.providerId)

											return (
												<div
													key={lineIndex}
													style={{
														display: 'flex',
														justifyContent:
															'space-between',
														alignItems: 'center',
														marginBottom: '10px',
													}}
												>
													<div
														style={{
															flex: 1,
														}}
													>
														<p
															style={{
																margin: 0,
															}}
														>
															{prestador}
														</p>
														<small
															style={{
																display: 'flex',
																alignItems:
																	'center',
															}}
														>
															{symbol}
															<span
																style={{
																	color: 'inherit',
																}}
															>
																{valorData}{' '}
																{distancia}{' '}
																{rapidez}{' '}
															</span>
														</small>
													</div>
													<button
														type='button'
														onClick={() => {
															if (!provider)
																return
															if (
																selectedProvider ===
																lineIndex
															) {
																setSelectedProvider(
																	null
																)
																setValue(
																	'providerId',
																	null
																)
																setPendingProvider(
																	null
																)
																setSelectedUserJustification(
																	null
																)
																setSelectedUserJustificationId(
																	null
																)
																return
															}
															if (
																isFirstProvider
															) {
																setValue(
																	'providerId',
																	provider.providerId.toString()
																)
																setSelectedProvider(
																	lineIndex
																)
																setSelectedUserJustification(
																	'Sugestao do modelo.'
																)
																setSelectedUserJustificationId(
																	null
																)
																setPendingProvider(
																	null
																)
															} else {
																setValue(
																	'providerId',
																	provider.providerId.toString()
																)
																setSelectedProvider(
																	lineIndex
																)
																setPendingProvider(
																	provider
																)
															}
														}}
														style={{
															backgroundColor:
																selectedProvider ===
																lineIndex
																	? '#4caf50'
																	: '#ffffff',
															border:
																selectedProvider ===
																lineIndex
																	? '1px solid #4caf50'
																	: '1px solid #e0e0e0',
															borderRadius: '4px',
															cursor: 'pointer',
															fontSize: '10px',
															fontWeight: '500',
															whiteSpace:
																'nowrap',
															transition:
																'all 0.15s ease',
														}}
														onMouseEnter={(e) => {
															if (
																selectedProvider !==
																lineIndex
															) {
																e.currentTarget.style.backgroundColor =
																	'#f0f0f0'
																e.currentTarget.style.borderColor =
																	'#ccc'
															}
														}}
														onMouseLeave={(e) => {
															if (
																selectedProvider !==
																lineIndex
															) {
																e.currentTarget.style.backgroundColor =
																	'#ffffff'
																e.currentTarget.style.borderColor =
																	'#e0e0e0'
															}
														}}
													>
														<span
															style={{
																color:
																	selectedProvider ===
																	lineIndex
																		? '#ffffff'
																		: '#555',
															}}
														>
															✓
														</span>
													</button>
												</div>
											)
										} else {
											return null
										}
									})
							) : (
								<p>
									{prestadorData ||
										'Erro ao tentar carregar os prestadores sugeridos'}
								</p>
							)}
							{mensagemFinal && (
								<p
									style={{
										marginTop: '10px',
										color: 'black',
										fontSize: '11px',
									}}
								>
									{mensagemFinal
										.replace(/['"()]/g, '')
										.trim()}
								</p>
							)}
						</>
					)}
					{pendingProvider && (
						<>
							{showWarning && !selectedUserJustification && (
								<AlertBox
									className={styles['alert-box']}
									type='warning'
									items={[
										{
											title: i18n`Warning`,
											text: i18n`Justification is mandatory to proceed`,
										},
									]}
								/>
							)}
							<JustificationField
								justifications={justifications}
								providerName={pendingProvider.providerName}
								onJustificationChange={
									handleJustificationChange
								}
								onCancel={handleJustificationCancel}
							/>
						</>
					)}
				</fieldset>
			)}

			{(isAdministrative ||
				requisition?.type?.id === 4 ||
				requisition?.type?.id === 2) && (
				<TaskForwardingController
					outsideNetwork={outsideNetwork}
					districts={districts}
					serviceProviders={serviceProviders}
					providerDescription={requisition?.providerDescription}
					requisitionOutsideNetwork={requisition?.outsideNetwork}
					districtName={requisition?.district?.name}
					branch={accidentType}
					selected_insurance={
						processContent?.insurancePolicy?.insuranceCompany?.id
					}
					select_act={selectedMedicalActCodes}
					codpost7={postalCode}
					onUpdateFormData={onUpdateFormData}
					requisitionId={requisition?.id}
					injuredBirthday={injuredBirthday}
					injuredType={injuredType}
					bodyPart={bodyPart}
					requestingProviderId={
						requisition?.requiringEstablishment?.id
					}
					appointmentDate={requisition?.appointment?.date?.UTC}
					scheduleDate={requisition?.scheduledDate?.UTC}
					requisitionType={requisition?.type?.id}
					externalSuggestions={jsonResponseRef.current}
					onBlockResolver={(delta) => setBlockCount((c) => c + delta)}
					hideJustification={requisition?.type?.id === 2}
					showWarning={showWarning}
					onProviderPendingChange={handleProviderPendingChange}
					requisitionStateId={requisition?.status?.id}
				/>
			)}

			{isAdministrative && requiresChargeAmount && (
				<TaskPaymentsController />
			)}

			<fieldset className={styles['observation-container']}>
				{isAdministrative ? (
					<ExpandableWidget
						className={styles['observation']}
						heading='h5'
						title={i18n`Administrative Observations`}
					>
						<HFTextInput
							name='administrativeObservation'
							placeholder={i18n`Administrative observation (optional)`}
							multiline={true}
							rows={5}
							noResize
						/>
					</ExpandableWidget>
				) : (
					<ExpandableWidget
						className={styles['observation']}
						heading='h5'
						title={i18n`Clinical Observations`}
					>
						<HFTextInput
							name='medicalObservation'
							placeholder={i18n`Clinical observation (optional)`}
							multiline={true}
							rows={5}
							noResize
						/>
					</ExpandableWidget>
				)}
			</fieldset>
		</>
	)
}

export const MedicalActsClinicalApprovalMenu = (props) => {
	const { data } = props
	const { formId, i18n, history, submitting, setSubmissionType } = data

	const options = [
		{
			value: 'forward',
			text: i18n`Forward`,
			action: () => {
				history.push('/forward')
			},
		},
		{
			value: 'refuse',
			text: i18n`Refuse`,
			action: () => {
				history.push('/refuse')
			},
		},
	]

	return (
		<menu>
			<ModalOptions
				title={i18n`Options`}
				options={options}
				onSelect={(item, e) => {
					item && item.action && item.action()
				}}
			>
				<button data-wt-id='options' className={styles['menu-button']}>
					{i18n`Options`}
				</button>
			</ModalOptions>
			<button
				primary='true'
				onClick={() => {
					setSubmissionType('approve')
				}}
				form={`form_${formId}`}
			>
				{submitting ? i18n`Approving` : i18n`Approve`}
			</button>
		</menu>
	)
}
